import type {Nullish} from 'shared/web';

type CatalogueRecord = {
  id: string;
  name: string;
  vehicleDataKey?: string | Nullish;
  archived?: boolean;
};
type CatalogueModelRecord = CatalogueRecord & {makeId: string};
/** What the author picked or typed: a name, and optionally a catalogue key with its label. */
export type WantedIdentity = {name: string; vehicleDataKey?: string | Nullish; label?: string};

/**
 * How an existing record relates to what the author wants:
 * - `key`: already linked to the picked catalogue key, use it as it is.
 * - `link`: same name and not linked yet, link it to the picked key instead of creating another.
 * - `name`: same name (letter case aside) but linked elsewhere or nothing was picked; a new record
 *   would be rejected as a duplicate, so only using this one is possible.
 * - `archived`: an archived record already holds the name, which blocks creating it again.
 */
export type IdentityMatch<T> = {record: T; kind: 'key' | 'link' | 'name' | 'archived'};
export type CatalogueIdentity<T> = {
  match?: IdentityMatch<T>;
  /** Active, unlinked records whose name only resembles the pick; the author decides. */
  similar: T[];
};

/** Case, diacritics, punctuation and padding aside: "Škoda" = "skoda", "Mercedes-Benz" = "mercedes benz". */
const normalizeName = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/** The readable tail of a catalogue key: MAKE_MERCEDES_BENZ → "mercedes benz", …-MODELFAMILY_KUGA → "kuga". */
const keyName = (key: string) =>
  normalizeName(key.split('-MODELFAMILY_')[1] ?? key.replace(/^MAKE_/, ''));

const isWordPrefix = (short: string, long: string) => long.startsWith(`${short} `);

function resolveIdentity<T extends CatalogueRecord>(
  records: T[],
  wanted: WantedIdentity
): CatalogueIdentity<T> {
  const key = wanted.vehicleDataKey || '';
  const byKey = key
    ? records.find((record) => !record.archived && record.vehicleDataKey === key)
    : undefined;
  if (byKey) {
    return {match: {record: byKey, kind: 'key'}, similar: []};
  }

  const names = [
    ...new Set(
      [wanted.name, wanted.label ?? '', key ? keyName(key) : ''].map(normalizeName).filter(Boolean)
    ),
  ];
  const sameName = records.find((record) => names.includes(normalizeName(record.name)));
  if (sameName) {
    const kind = sameName.archived ? 'archived' : key && !sameName.vehicleDataKey ? 'link' : 'name'; // eslint-disable-line no-nested-ternary -- Pre-existing violation
    return {match: {record: sameName, kind}, similar: []};
  }
  if (!key) {
    return {similar: []};
  }
  const similar = records.filter((record) => {
    const name = normalizeName(record.name);
    return (
      !record.archived &&
      !record.vehicleDataKey &&
      name.length > 0 &&
      names.some((wantedName) => isWordPrefix(name, wantedName) || isWordPrefix(wantedName, name))
    );
  });
  return {similar};
}

/** The existing make an author means by a catalogue pick or a typed name, archived ones included. */
export const resolveMakeIdentity = <T extends CatalogueRecord>(
  makes: T[],
  wanted: WantedIdentity
) => resolveIdentity(makes, wanted);

/** Model names and keys are only unique under one make. */
export const resolveModelIdentity = <T extends CatalogueModelRecord>(
  models: T[],
  makeId: string,
  wanted: WantedIdentity
) =>
  resolveIdentity(
    models.filter((model) => model.makeId === makeId),
    wanted
  );

/** An active record other than `selfId` that already holds the key; the backend refuses a second. */
export const findKeyOwner = <T extends CatalogueRecord>(
  records: T[],
  key: string | Nullish,
  selfId?: string
) =>
  key
    ? records.find(
        (record) => !record.archived && record.id !== selfId && record.vehicleDataKey === key
      )
    : undefined;

/** A model family key names its make, so it is only valid under that make's own key. */
export const isModelKeyOfMake = (modelKey: string | Nullish, makeKey: string | Nullish) =>
  Boolean(modelKey && makeKey && modelKey.startsWith(`${makeKey}-MODELFAMILY_`));
